import React from 'react'
import { X } from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'
import { Badge } from '@/shared/ui'
import { getTrendPointsDetail, type TrendPointsDetail, type TrendShadowRow } from '../services/trendShadowService'
import { computeEngineView, type EnginePoint, type PivotStage, type StructureMark } from '../services/trendEngineView'

const TF_LABEL: Record<string, string> = { '5': '5m', '15': '15m', '30': '30m', '60': '1h', '240': '4h', '720': '12h', D: '1D', W: '1S', M: '1M' }
const PHASE_LABEL: Record<string, string> = {
  range: 'lateral', pivoting_up: 'virando para cima', pivoting_down: 'virando para baixo',
  seeking_higher_low: 'buscando fundo mais alto', seeking_lower_high: 'buscando topo mais baixo',
}
const CHART_CANDLES = 120

// Estilo de cada estágio no gráfico (cor, tamanho do ponto e do texto)
const STAGE_STYLE: Record<PivotStage, { color: string; r: number; font: number; label: string; hollow?: boolean; dashed?: boolean }> = {
  confirmado: { color: '#38bdf8', r: 5, font: 11, label: 'Confirmado' },
  candidato: { color: '#a78bfa', r: 4.5, font: 9.5, label: 'Candidato', hollow: true },
  formacao: { color: '#f472b6', r: 5, font: 9, label: 'Em formação', hollow: true, dashed: true },
  interno: { color: '#9ca3af', r: 2.8, font: 8.5, label: 'Interno' },
}
const STAGE_HELP: Record<PivotStage, string> = {
  formacao: 'O preço ainda está fazendo este topo/fundo. Pode mudar a cada candle.',
  candidato: 'O topo/fundo já nasceu: para topo, um candle fechou abaixo da mínima do anterior; para fundo, fechou acima da máxima do anterior.',
  confirmado: 'Depois do candidato, nasceu o candidato do lado oposto (regra do Vela). O ponto está confirmado.',
  interno: 'Topos e fundos do caminho dentro da tendência. Não levam letra e não mudam a tendência.',
}
type MarkKey = 'HH' | 'HL' | 'LH' | 'LL'
// O que cada marca é, em cada tendência (um de cada; HH e LL sempre os extremos)
const ROLE_TEXT: Record<'bullish' | 'bearish', Record<MarkKey, string>> = {
  bullish: {
    LL: 'LL · fundo de onde a alta nasceu (fundo mínimo)',
    HH: 'HH · topo máximo da alta',
    HL: 'HL · fundo defendido (fechar abaixo = alta virando)',
    LH: 'LH · maior topo depois do HH',
  },
  bearish: {
    HH: 'HH · topo de onde a baixa nasceu (topo máximo)',
    LL: 'LL · fundo mínimo da baixa',
    LH: 'LH · topo defendido (fechar acima = baixa virando)',
    HL: 'HL · topo mais alto depois do LL (topo do repique)',
  },
}

function trendText(v: string | null | undefined) {
  if (v === 'bullish') return 'Alta'
  if (v === 'bearish') return 'Baixa'
  if (v === 'neutral') return 'Neutra'
  return '—'
}
function TrendBadge({ value }: { value: string | null | undefined }) {
  if (value === 'bullish') return <Badge variant="soft" color="success">Alta</Badge>
  if (value === 'bearish') return <Badge variant="soft" color="error">Baixa</Badge>
  return <Badge variant="soft">{trendText(value)}</Badge>
}
function fmtPrice(v: number | null | undefined) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—'
  return Number(v).toLocaleString('pt-BR', { maximumSignificantDigits: 8 })
}
function fmtDate(v: string | null | undefined) {
  if (!v) return '—'
  return new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

type ChartMark = { at: string; price: number; text: string; side: 'top' | 'bottom'; kind: PivotStage | 'ca' }
type ChartLine = { price: number; text: string; model: 'ca' | 'ts' }

type Point = { label: string; type?: string | null; price?: number | null; at?: string | null }

function MarkRow({ text, m }: { text: string; m: StructureMark | null | undefined }) {
  const st = m ? STAGE_STYLE[m.stage] : null
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/40 py-2 text-sm">
      <div className="text-foreground/80">
        {text}
        {m && st ? (
          <div className="mt-0.5 text-xs" style={{ color: st.color }}>
            <StageDot stage={m.stage} /> {st.label}
            {m.via === 'pavio' ? <span className="text-foreground/50"> · só pavio (sem fechamento além)</span> : null}
            {m.defended ? <span className="text-foreground/50"> · também é o defendido</span> : null}
          </div>
        ) : null}
      </div>
      <div className="text-right">
        <div>{fmtPrice(m?.price)}</div>
        <div className="text-xs text-foreground/50">{fmtDate(m?.at)}</div>
      </div>
    </div>
  )
}

function PointRow({ p, highlight }: { p: Point; highlight?: boolean }) {
  return (
    <div className={`flex items-start justify-between gap-3 border-b border-border/40 py-2 text-sm ${highlight ? 'font-medium' : ''}`}>
      <div className="text-foreground/70">{p.label}{p.type ? <span className="ml-1 text-foreground/50">({p.type})</span> : null}</div>
      <div className="text-right">
        <div>{fmtPrice(p.price)}</div>
        <div className="text-xs text-foreground/50">{fmtDate(p.at)}</div>
      </div>
    </div>
  )
}

function StageDot({ stage }: { stage: PivotStage }) {
  const s = STAGE_STYLE[stage]
  return (
    <svg width="14" height="14" className="inline-block align-middle">
      <circle cx="7" cy="7" r={Math.max(3, s.r)} fill={s.hollow ? 'none' : s.color} stroke={s.color} strokeWidth={1.6} strokeDasharray={s.dashed ? '2 2' : undefined} />
    </svg>
  )
}

// Gráfico: candles + linhas de topo/fundo e os pontos de cada modelo/estágio
function PointsChart({ candles, marks, lines }: { candles: TrendPointsDetail['candles']; marks: ChartMark[]; lines: ChartLine[] }) {
  if (!candles.length) return <div className="py-10 text-center text-sm text-foreground/60">Sem candles para desenhar.</div>
  const W = 900, H = 340, padL = 8, padR = 96, padT = 36, padB = 36
  const lo = Math.min(...candles.map((c) => c.low), ...lines.map((l) => l.price))
  const hi = Math.max(...candles.map((c) => c.high), ...lines.map((l) => l.price))
  const span = hi - lo || hi || 1
  const y = (p: number) => padT + (1 - (p - lo) / span) * (H - padT - padB)
  const step = (W - padL - padR) / candles.length
  const x = (i: number) => padL + i * step + step / 2
  const idxByTime = new Map(candles.map((c, i) => [Date.parse(c.open_time), i]))
  // posição na pilha de cada texto: conta pontos já desenhados no mesmo candle (ou vizinho) e lado
  const stackSlots = new Map<number, number>()
  const used = new Map<string, number>()
  marks.forEach((m, k) => {
    const i = idxByTime.get(Date.parse(m.at))
    if (i === undefined) return
    const near = [i - 1, i, i + 1].reduce((acc, j) => Math.max(acc, used.get(`${j}|${m.side}`) ?? 0), 0)
    stackSlots.set(k, near)
    used.set(`${i}|${m.side}`, near + 1)
  })

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full rounded-md bg-background/60">
      {candles.map((c, i) => {
        const up = c.close >= c.open
        const col = up ? '#10b981' : '#ef4444'
        const top = y(Math.max(c.open, c.close))
        const bodyH = Math.max(1, Math.abs(y(c.open) - y(c.close)))
        return (
          <g key={c.open_time}>
            <line x1={x(i)} x2={x(i)} y1={y(c.high)} y2={y(c.low)} stroke={col} strokeWidth={1} opacity={0.7} />
            <rect x={x(i) - step * 0.3} y={top} width={Math.max(1, step * 0.6)} height={bodyH} fill={col} opacity={0.8} />
          </g>
        )
      })}
      {lines.map((l, k) => {
        const col = l.model === 'ca' ? '#f59e0b' : '#38bdf8'
        return (
          <g key={`l${k}`}>
            <line x1={padL} x2={W - padR} y1={y(l.price)} y2={y(l.price)} stroke={col} strokeWidth={1.2} strokeDasharray={l.model === 'ca' ? '6 4' : undefined} />
            <text x={W - padR + 4} y={y(l.price) + 4} fontSize={11} fill={col}>{l.text}</text>
          </g>
        )
      })}
      {marks.map((m, k) => {
        const i = idxByTime.get(Date.parse(m.at))
        if (i === undefined) return null
        const st = m.kind === 'ca' ? { color: '#f59e0b', r: 3.5, font: 9.5, hollow: false, dashed: false } : STAGE_STYLE[m.kind]
        const cy = y(m.price)
        // Vários pontos no mesmo candle/lado: empilha os textos em vez de sobrepor
        const slot = stackSlots.get(k) ?? 0
        const ty = m.side === 'top' ? cy - st.r - 4 - slot * 11 : cy + st.r + st.font + slot * 11
        return (
          <g key={`m${k}`}>
            <circle cx={x(i)} cy={cy} r={st.r} fill={st.hollow ? 'rgba(0,0,0,0.35)' : st.color} stroke={st.color} strokeWidth={st.hollow ? 1.6 : 0.6} strokeDasharray={st.dashed ? '2 2' : undefined} />
            {m.text ? <text x={x(i)} y={ty} fontSize={st.font} textAnchor="middle" fill={st.color} fontWeight={m.kind === 'confirmado' ? 600 : 400}>{m.text}</text> : null}
          </g>
        )
      })}
    </svg>
  )
}

const ALL_KINDS: (PivotStage | 'ca')[] = ['confirmado', 'candidato', 'formacao', 'interno', 'ca']

export default function TrendPointsModal({ row, onClose }: { row: TrendShadowRow | null; onClose: () => void }) {
  const [detail, setDetail] = React.useState<TrendPointsDetail | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [visible, setVisible] = React.useState<Record<string, boolean>>({ confirmado: true, candidato: true, formacao: true, interno: true, ca: false })

  React.useEffect(() => {
    if (!row) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setDetail(null)
    getTrendPointsDetail(row.symbol, row.timeframe)
      .then((d) => { if (!cancelled) setDetail(d) })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'Falha ao carregar os pontos') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [row?.symbol, row?.timeframe])

  // Motor rodando no navegador com os mesmos 500 candles da sombra
  const engine = React.useMemo(() => {
    if (!detail || !row) return null
    try { return computeEngineView(row.timeframe, detail.candles) } catch { return null }
  }, [detail, row?.timeframe])

  if (!row) return null
  const s = engine?.snapshot ?? detail?.summary ?? {}
  // Estrutura calculada pelo próprio motor (um HH/HL/LH/LL por tendência)
  const st = (engine?.structure ?? s.structure ?? null) as Record<string, any> | null
  const chartCandles = detail ? detail.candles.slice(-CHART_CANDLES) : []
  const windowStart = chartCandles.length ? Date.parse(chartCandles[0].open_time) : 0
  const windowPoints = (engine?.points ?? []).filter((p) => Date.parse(p.at) >= windowStart)

  // Cripto Alert: último pivô de cada classificação (por fechamento) e níveis de rompimento (por pavio)
  const lastCa = (cls: string) => detail?.caPivots.find((p) => p.classification === cls) ?? null
  const caHH = lastCa('HH'), caLL = lastCa('LL'), caHL = lastCa('HL'), caLH = lastCa('LH')
  const caAbove = detail?.caLevels.find((l) => l.break_direction === 'break_above') ?? null
  const caBelow = detail?.caLevels.find((l) => l.break_direction === 'break_below') ?? null

  const marks: ChartMark[] = []
  const lines: ChartLine[] = []
  for (const p of windowPoints) {
    if (!visible[p.stage]) continue
    // internos sem letra: só as 4 marcas da tendência levam nome
    marks.push({ at: p.at, price: p.price, side: p.side, kind: p.stage, text: p.stage === 'interno' ? '' : p.label })
  }
  if (visible.ca) {
    for (const p of [caHH, caLL, caHL, caLH]) {
      if (p) marks.push({ at: p.pivot_time, price: Number(p.pivot_close), text: p.classification, side: p.classification.endsWith('H') ? 'top' : 'bottom', kind: 'ca' })
    }
    if (caAbove) lines.push({ price: Number(caAbove.level), text: 'CA topo', model: 'ca' })
    if (caBelow) lines.push({ price: Number(caBelow.level), text: 'CA fundo', model: 'ca' })
  }

  // Topo/fundo de referência do motor (na alta: HH e HL defendido; na baixa: LH defendido e LL)
  const tsTop = s.validHighPrice != null ? { type: s.validHighType, price: Number(s.validHighPrice), at: s.validHighAt } : null
  const tsBottom = s.validLowPrice != null ? { type: s.validLowType, price: Number(s.validLowPrice), at: s.validLowAt } : null
  if (tsTop) lines.push({ price: tsTop.price, text: `TS topo (${tsTop.type ?? ''})`, model: 'ts' })
  if (tsBottom) lines.push({ price: tsBottom.price, text: `TS fundo (${tsBottom.type ?? ''})`, model: 'ts' })
  const trendKey: 'bullish' | 'bearish' | null = st?.trend === 'bullish' || st?.trend === 'bearish' ? st.trend : null
  const markOrder: MarkKey[] = trendKey === 'bearish' ? ['HH', 'LH', 'HL', 'LL'] : ['LL', 'HL', 'LH', 'HH']

  const timeline: EnginePoint[] = [...windowPoints].reverse()

  return (
    <Modal open={!!row} onOpenChange={(v) => !v && onClose()} className="max-w-6xl">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="text-xl font-semibold">{row.symbol} · {TF_LABEL[row.timeframe] ?? row.timeframe}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-foreground/70">
            Cripto Alert <TrendBadge value={row.trend_atual} /> · Trend Structure <TrendBadge value={s.trend ?? row.trend_v2} />
            {s.phase ? <span className="text-foreground/60">({PHASE_LABEL[s.phase] ?? s.phase})</span> : null}
          </div>
        </div>
        <button onClick={onClose} className="rounded-md p-1 text-foreground/60 hover:bg-foreground/10" aria-label="Fechar"><X className="h-5 w-5" /></button>
      </div>

      {error && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm">{error}</div>}
      {loading && <div className="py-10 text-center text-sm text-foreground/60">Carregando os pontos...</div>}

      {detail && (
        <>
          <div className="mb-2 flex flex-wrap items-center gap-3 text-xs">
            <span className="text-foreground/60">Mostrar:</span>
            {ALL_KINDS.map((k) => (
              <label key={k} className="flex cursor-pointer items-center gap-1.5 text-foreground/80">
                <input type="checkbox" checked={!!visible[k]} onChange={(e) => setVisible((v) => ({ ...v, [k]: e.target.checked }))} />
                {k === 'ca' ? <span style={{ color: '#f59e0b' }}>● Cripto Alert (atual)</span> : <><StageDot stage={k} />{STAGE_STYLE[k].label}</>}
              </label>
            ))}
          </div>

          <PointsChart candles={chartCandles} marks={marks} lines={lines} />

          <div className="mb-5 mt-3 grid gap-2 rounded-md border border-border/40 p-3 text-xs text-foreground/70 md:grid-cols-2">
            {(['formacao', 'candidato', 'confirmado', 'interno'] as PivotStage[]).map((k) => (
              <div key={k} className="flex gap-2">
                <StageDot stage={k} />
                <div><b style={{ color: STAGE_STYLE[k].color }}>{STAGE_STYLE[k].label}:</b> {STAGE_HELP[k]}</div>
              </div>
            ))}
            <div className="flex gap-2 md:col-span-2">
              <span className="mt-1 inline-block h-0 w-4" style={{ borderTop: '2px solid #38bdf8' }} />
              <div><b className="text-sky-400">TS topo / TS fundo:</b> referências da tendência. Na alta: o HH (topo máximo) e o HL defendido. Na baixa: o LH defendido e o LL (fundo mínimo). Só existe um HH, um HL, um LH e um LL por tendência; HH e LL são sempre os extremos.</div>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-sky-500/30 p-4">
              <div className="mb-1 flex items-center justify-between">
                <div className="font-semibold text-sky-400">Trend Structure (Vela)</div>
                <TrendBadge value={s.trend ?? row.trend_v2} />
              </div>
              <div className="mb-3 text-xs text-foreground/60">
                Estrutura calculada pelo motor: uma marca de cada na tendência atual.
              </div>
              {trendKey ? (
                markOrder.map((k) => <MarkRow key={k} text={ROLE_TEXT[trendKey][k]} m={(st?.[k.toLowerCase()] ?? null) as StructureMark | null} />)
              ) : (
                <div className="py-4 text-sm text-foreground/60">Sem tendência definida nesta janela.</div>
              )}
            </div>

            <div className="rounded-lg border border-amber-500/30 p-4">
              <div className="mb-1 flex items-center justify-between">
                <div className="font-semibold text-amber-500">Cripto Alert (atual)</div>
                <TrendBadge value={row.trend_atual} />
              </div>
              <div className="mb-3 text-xs text-foreground/60">
                Regra: <b>Alta</b> quando o último HH (fechamento) é mais recente que o último LL; <b>Baixa</b> no caso contrário.
              </div>
              <PointRow p={{ label: 'Último HH', price: caHH?.pivot_close, at: caHH?.pivot_time }} highlight={row.trend_atual === 'bullish'} />
              <PointRow p={{ label: 'Último LL', price: caLL?.pivot_close, at: caLL?.pivot_time }} highlight={row.trend_atual === 'bearish'} />
              <PointRow p={{ label: 'Último HL', price: caHL?.pivot_close, at: caHL?.pivot_time }} />
              <PointRow p={{ label: 'Último LH', price: caLH?.pivot_close, at: caLH?.pivot_time }} />
              <PointRow p={{ label: 'Topo a romper (pavio)', price: caAbove?.level, at: caAbove?.pivots?.pivot_time }} />
              <PointRow p={{ label: 'Fundo a romper (pavio)', price: caBelow?.level, at: caBelow?.pivots?.pivot_time }} />
            </div>
          </div>

          <div className="mb-2 mt-6 text-sm font-semibold">Pontos da Trend Structure (janela do gráfico)</div>
          <div className="max-h-72 overflow-y-auto rounded-md border border-border/40">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted">
                <tr className="text-left">
                  <th className="px-2 py-1.5">Estágio</th>
                  <th className="px-2 py-1.5">Marca</th>
                  <th className="px-2 py-1.5">Preço</th>
                  <th className="px-2 py-1.5">Candle</th>
                  <th className="px-2 py-1.5">O que é</th>
                </tr>
              </thead>
              <tbody>
                {timeline.map((p, k) => (
                  <tr key={`${p.label}-${p.at}-${k}`} className="border-t border-border/30">
                    <td className="px-2 py-1.5 whitespace-nowrap"><StageDot stage={p.stage} /> <span style={{ color: STAGE_STYLE[p.stage].color }}>{STAGE_STYLE[p.stage].label}</span></td>
                    <td className="px-2 py-1.5">{p.label}</td>
                    <td className="px-2 py-1.5">{fmtPrice(p.price)}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(p.at)}</td>
                    <td className="px-2 py-1.5 text-foreground/60">
                      {p.stage === 'interno' ? `${p.label} interno` : trendKey ? ROLE_TEXT[trendKey][p.label as MarkKey] ?? p.label : p.label}
                      {p.via === 'pavio' ? ' · só pavio' : ''}
                    </td>
                  </tr>
                ))}
                {timeline.length === 0 && (
                  <tr><td colSpan={5} className="px-2 py-6 text-center text-foreground/60">Nenhum ponto nesta janela.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  )
}
